import { useState, useMemo, useCallback } from 'react';

import { ArrowBackIos as ArrowBackIcon } from '@mui/icons-material';
import { Box, Button, Grid, Link } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import { withRouter } from 'providers';
import { flowRight as compose } from 'lodash';

import AxlDialog from 'components/AxlDialog';
import TimeWindow from 'components/TimeWindow';
import AssignmentAssign from 'components/AssignmentAssign';
import AssignmentNote from 'components/AssignmentNote';
import JMiniStopBox from 'components/JMiniStopBox';
import acl from 'constants/acl';
import { convertMeterToMile } from 'constants/common';
import { getStyles } from './styles';

function AssignmentMiniDetail(props) {
  const theme = useTheme();
  const styles = getStyles(theme);
  const { assignmentInfo, onBack, reloadData, store } = props;

  const [showUnassignDriver, setShowUnassignDriver] = useState(false);
  const [reason, setReason] = useState('');

  const { shipments, stops, clients, shipmentLabels } = assignmentInfo;

  const processedStops = useMemo(() => {
    return stops
      .filter((st) => st.type === 'DROP_OFF')
      .map((st) => {
        st.shipment = shipments.filter((sh) => sh.id === st.shipment_id).shift();
        st.client = clients?.filter((cl) => cl.id === st.client_id).shift();
        st.label = shipmentLabels?.filter((lb) => lb.shipment_id === st.shipment_id).shift();
        return st;
      });
  }, [stops, shipments, clients, shipmentLabels]);

  const hideUnassignDriver = useCallback(() => {
    setShowUnassignDriver(false);
  }, []);

  const updateReason = useCallback(() => (e) => {
    setReason(e);
  }, []);

  const onUnAssignDriver = useCallback(() => {
    if (!reason) return;
    const { assignmentStore } = store;

    assignmentStore.unassign(assignmentInfo, reason).then((r) => {
      if (r.ok) onBack(reloadData);
    });
  }, [reason, store, assignmentInfo, onBack, reloadData]);

  const { assignment, driver } = assignmentInfo;
  const { user = {} } = store.userStore;
  const status = assignment.status || 'PENDING';
  const { permissionStore } = store;

  const unAssignOptions = {
    props: {
      bg: 'none',
      tiny: true,
    },
    onClick: () => {
      hideUnassignDriver();
      onUnAssignDriver();
    },
    text: `Unassign Driver - [${driver.id}] ${driver.first_name} ${driver.last_name}`,
    title: 'Unassign Driver',
  };

  const isDeniedUnassign = permissionStore.isDenied(acl.ASSIGNMENTS.ASSIGN);

  return (
    <Box p={2} sx={styles.container}>
      <Box mb={2}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid>
            <ArrowBackIcon sx={styles.backItem} color="primary" onClick={() => onBack()} fontSize="small" />
            <Link sx={styles.backItem} onClick={() => onBack()} underline="always" color="primary">
              Back to Pending Route List
            </Link>
          </Grid>
          <Grid>
            {!isDeniedUnassign && (
              <Box mx={1} component="span">
                <Button
                  variant="contained"
                  size="small"
                  disableElevation
                  color="secondary"
                  onClick={() => setShowUnassignDriver(true)}
                >
                  Unassign
                </Button>
              </Box>
            )}
            <Box component="span">
              <Button
                variant="contained"
                size="small"
                target="_blank"
                disableElevation
                href={`/routes/${moment(assignment.predicted_end_ts).format('YYYY-MM-DD')}/all/${assignment.id}`}
              >
                View in dispatch
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Box sx={styles.infoContainer}>
        <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
          <Grid>
            <Box component="span" sx={styles.label}>
              {assignment.label}
            </Box>
            <Box component="code" sx={styles.code}>{assignment.id}</Box>
          </Grid>
          <Grid>
            <Box>Assignment Status</Box>
            <Box sx={{ ...styles.bold, ...styles[status.trim().toLowerCase()] }}>{status}</Box>
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={styles.assignmentInfo}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box py={0.5}>
              <span>Type: </span>
              <Box component="span" sx={styles.bold}>{shipments?.[0]?.service_level ?? '-'}</Box>
            </Box>
            <Box py={0.5}>
              <span>Volume: </span>
              <Box component="span" sx={styles.bold}>{shipments?.length ?? 0}</Box>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box py={0.5}>
              <span>Est. Time Window: </span>
              <TimeWindow
                variant="tooltip"
                startTs={assignment.predicted_start_ts}
                endTs={assignment.predicted_end_ts}
                timezone={assignment.timezone}
                containerSx={styles.timeWindow}
                valueSx={styles.timeWindowValue}
              />
            </Box>
            <Box py={0.5}>
              <span>Est. Distance: </span>
              <Box component="span" sx={styles.bold}>
                {convertMeterToMile(assignment.travel_distance)} {`mi`}
              </Box>
            </Box>
          </Grid>
        </Grid>
        <Box py={1}>
          <AssignmentNote assignmentId={assignment.id} userId={user.id} />
        </Box>
      </Box>
      <Box>
        <Box pb={1} align="left" sx={styles.bold}>
          Shipment List:
        </Box>
        <Box p={2} sx={styles.shipmentList}>
          {processedStops.map((stop) => (
            <JMiniStopBox onClick={() => null} key={stop.id} stop={stop} />
          ))}
        </Box>
      </Box>
      <AxlDialog
        isOpen={showUnassignDriver}
        handleClose={hideUnassignDriver}
        childrenTitle={unAssignOptions.text}
        maxWidth="sm"
      >
        <AssignmentAssign {...unAssignOptions} updateReason={updateReason()} onClose={hideUnassignDriver} />
      </AxlDialog>
    </Box>
  );
}

export default compose(withRouter, inject('store'), observer)(AssignmentMiniDetail);
